import { memo, useCallback, useMemo } from "react";
import { IGif, IUser } from "@/common/services/types";
import { UserInfo } from "../UserInfo";
import { BaseButton } from "components/buttons/BaseButton";
import { clsx } from "clsx";
import { CurrentUserObject } from "@/common/hooks/useVisitorInfo";
import { GifStats } from "../GifStats";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { EventSource, metricsBus } from "@/common/buses";
import { useUserSubscription } from "@/common/hooks/useUserSubscription";
import "./MetaInfo.scss";

export interface IMetaInfoProps {
  gif: IGif;
  user: IUser | undefined;
  currentUser: CurrentUserObject | null;
  isVisible: boolean;
  position: number;
  galleryImagesQty: number;
  playCount: number;
}

export const MetaInfo = memo(({
  gif,
  user,
  currentUser,
  galleryImagesQty,
  playCount,
  position
}: IMetaInfoProps) => {
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const onProfileMetric = useCallback((value: string) => {
    metricsBus.emit("profileClicked", {
      gifId: value,
      source: eventSource?.name ?? EventSource.Other,
      sourceId: eventSource?.id ?? null,
      position,
      playCount
    });
  }, [eventSource, position, playCount]);

  const hasCta = useMemo(() => gif.cta && gif.cta.text && gif.cta.link, [gif.cta]);

  const { subscribed, subscribe, unsubscribe } = useUserSubscription(gif.userName);

  const onSubscribe = useCallback(() => {
    subscribed ? unsubscribe() : subscribe({ gifId: gif.id, position, playCount });
  }, [gif, position, playCount, subscribed]);

  return (
    <div className={clsx("metaInfo", hasCta && "metaInfo_isBoosted")}>
      {user && <UserInfo
        isFollowing={subscribed}
        onFollow={onSubscribe}
        currentUser={currentUser}
        gif={gif}
        user={user}
        onProfileMetric={onProfileMetric}
      />}
      <GifStats gif={gif} galleryImagesQty={galleryImagesQty} />
      {hasCta && (
        <div className="boostedBtn">
          <BaseButton
            aria-label={gif.cta?.text}
            onClick={() => window.open(gif.cta?.link, "_blank", "noopener,noreferrer")}
          >{gif.cta?.text}</BaseButton>
        </div>
      )}
    </div>
  );
});
